import { useCallback } from "react";
import { useIonModal } from "@ionic/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useHistory } from "react-router-dom";
import { createRequestDetailsPath } from "routes/routeData";

import {
  ALERT_COLOR_KEY,
  ALERT_MESSAGE_KEY,
  ALERT_TITLE_KEY,
} from "~components/RequestInfoCard/constants";
import { translate } from "~utils";

import { RequestModel } from "../../model/RequestModel";
import { cancelRequest } from "../../utils/api";
import {
  CancelRequestModal,
  type CancelRequestResponse,
  type CancelRequestState,
} from "../CancelRequest";

export const useCancelRequest = (request: RequestModel) => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (payload: Record<string, string | boolean>) => {
      return cancelRequest(request.id, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["requests"] });
    },
  });

  const onCancelRequestSubmit = async (
    state: CancelRequestState,
  ): Promise<{ isSuccess: boolean; data?: CancelRequestResponse }> => {
    try {
      const response = await mutation.mutateAsync({
        reason: state?.cancelReason || "",
        explanation: state.cancelExplanation || "",
        "should-repost": state.shouldRepost || false,
      });

      return {
        isSuccess: true,
        data: {
          reason: response.data["reason"],
          explanation: response.data["explanation"],
          requestId: response.data["request-id"],
          repostedRequestId: response.data["reposted-request-id"],
        } as CancelRequestResponse,
      };
    } catch {
      return { isSuccess: false };
    }
  };

  const [presentCancelRequestModal, dismissCancelRequestModal] = useIonModal(CancelRequestModal, {
    request,
    onDismiss: (data?: unknown, role?: string) => {
      dismissCancelRequestModal(data, role);
    },
    onSubmit: onCancelRequestSubmit,
  });

  const openCancelRequestModal = useCallback(() => {
    presentCancelRequestModal({
      onDidDismiss: (event?: unknown) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const data = (event as any)?.detail.data;
        const repostedId = data?.response?.repostedRequestId;

        if (repostedId) {
          history.push({
            pathname: createRequestDetailsPath("active-requests", repostedId),
            state: {
              [ALERT_TITLE_KEY]: translate("CANCEL_REQ_LABEL_HEADER_REPOSTED"),
              [ALERT_MESSAGE_KEY]: translate("CANCEL_REQ_LABEL_REPOST_ALERT_MESSAGE"),
              [ALERT_COLOR_KEY]: "success",
            },
          });
        }
      },
    });
  }, [presentCancelRequestModal, history]);

  return { openCancelRequestModal };
};
